import { createReducer, on } from '@ngrx/store';
import { AuthConfig } from 'angular-oauth2-oidc';
import * as d3 from 'd3-scale';

import * as PlatformActions from './platform.actions';
import { DialogInfo, initialPlatformState, PermissionAction } from './platform.state';
import * as RouterActions from './router.actions';
import { createRequestError, ResourceStatuses } from './shared/resource.state';

function setDialogToClosed(dialogs: DialogInfo[], id: string, data?: any): DialogInfo[] {
  return dialogs.map((d) => {
    if (d.id === id) {
      return { id, isOpen: false, data };
    } else {
      return { ...d };
    }
  });
}

export const platformReducer = createReducer(
  initialPlatformState,
  on(PlatformActions.loadPortalSettings, (state) => ({
    ...state,
    portalSettings: {
      ...state.portalSettings,
      status: ResourceStatuses.PENDING,
    },
  })),

  on(PlatformActions.loadPortalSettingsSucceeded, (state, { portalSettings }) => ({
    ...state,
    portalSettings: {
      status: ResourceStatuses.SUCCEEDED,
      data: {
        ...portalSettings,
        oidcConfig: {
          ...new AuthConfig(portalSettings.oidcConfig),
          showDebugInformation: true,
        },
        authorizePolicies: portalSettings.authorizePolicies.map((policy) => ({
          name: policy.name,
          permissions: policy.permissions.map((p) => ({
            resourceId: [...p.resourceId],
            action: p.action.toLowerCase() as PermissionAction,
          })),
        })),
      },
      error: null!,
    },
  })),

  on(PlatformActions.loadPortalSettingsFailed, (state, { status, errors }) => ({
    ...state,
    portalSettings: {
      ...state.portalSettings,
      status: ResourceStatuses.FAILED,
      error: createRequestError(status, errors),
    },
  })),

  on(PlatformActions.setIdentityClaims, (state, { identityClaims }) => ({
    ...state,
    auth: {
      ...state.auth,
      identityClaims: { ...identityClaims },
    },
  })),

  on(PlatformActions.loadCurrentUser, (state) => ({
    ...state,
    auth: {
      ...state.auth,
      user: {
        ...state.auth.user,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(PlatformActions.loadCurrentUserSucceeded, (state, { user }) => ({
    ...state,
    auth: {
      ...state.auth,
      user: {
        ...state.auth.user,
        status: ResourceStatuses.SUCCEEDED,
        data: {
          id: user.id,
          name: user.name,
          firstName: user.firstName,
          lastName: user.lastName,
          isInternal: user.isInternal,
          isActive: user.isActive,
          mail: user.mail,
          mobileNumber: user.mobileNumber,
          organizationUnit: user.organizationUnit,
          phoneNumber: user.phoneNumber,
          thumbnailPhoto: user.thumbnailPhoto,
          language: user.language,
          activeUntil: user.activeUntil,
          permissions: user.permissions.map((p) => ({
            resourceId: p.resourceId.map((r) => r?.toLowerCase()),
            actions: p.actions.map((a) => a?.toLowerCase()),
          })),
        },
        error: null!,
      },
    },
  })),

  on(PlatformActions.loadCurrentUserFailed, (state, { status, errors }) => ({
    ...state,
    auth: {
      ...state.auth,
      user: {
        ...state.auth.user,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(PlatformActions.dialogOpened, (state, { id }) => ({
    ...state,
    dialog: {
      ...state.dialog,
      dialogs: [...state.dialog.dialogs, { id, isOpen: true }],
    },
  })),

  on(PlatformActions.dialogClosed, (state, { id, data }) => ({
    ...state,
    dialog: {
      ...state.dialog,
      dialogs: setDialogToClosed(state.dialog.dialogs, id, data),
    },
  })),

  on(PlatformActions.showProgressBar, (state) => ({
    ...state,
    progressBar: {
      ...state.progressBar,
      visible: true,
      progress: 0,
      visibleProgress: 0,
    },
  })),

  on(PlatformActions.hideProgressBar, (state) => ({
    ...state,
    progressBar: {
      ...state.progressBar,
      visible: false,
    },
  })),

  on(PlatformActions.increaseProgressBar, (state) => {
    const progress = Math.min(state.progressBar.progress + 5, 100);
    const scaleLog = d3.scaleLog().domain([1, 100]).range([0, 10000]);

    return {
      ...state,
      progressBar: {
        ...state.progressBar,
        visible: true,
        progress,
        visibleProgress: Math.round(scaleLog(progress) / 100),
      },
    };
  }),

  on(RouterActions.setPath, (state, { path }) => {
    if (path.localeCompare(state.router.currentPath!) === 0) {
      return {
        ...state,
      };
    }

    return {
      ...state,
      router: {
        ...state.router,
        lastPath: state.router.currentPath,
        currentPath: path,
      },
    };
  }),

  on(RouterActions.back, (state) => ({
    ...state,
    router: {
      ...state.router,
      lastPath: null,
      currentPath: null,
    },
  })),

  on(RouterActions.home, (state) => ({
    ...state,
    router: {
      ...state.router,
      lastPath: null,
      currentPath: null,
    },
  })),

  on(RouterActions.clearPath, (state) => ({
    ...state,
    router: {
      ...state.router,
      lastPath: null,
      currentPath: null,
    },
  })),
);
